import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Layers, ChevronRight, Plus, Compass } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import {
  Header,
  Card,
  FAB,
  EmptyState,
  Button,
} from '../../components/ui';
import { BottomSheet } from '../../components/ui/BottomSheet';
import { groupsApi } from '../../api/groups';
import { athletesApi } from '../../api/athletes';
import { useSubscriptionLimits } from '../../hooks/useSubscriptionLimits';
import type { Athlete } from '../../types/api';

// -- Helpers ------------------------------------------------------------------

const GROUP_ICON_COLORS = [
  '#F57C00', // var(--avatar-1)
  '#E65100', // var(--avatar-2)
  '#FB8C00', // var(--avatar-3)
  '#FF9800', // var(--avatar-4)
  '#FFA726', // var(--avatar-5)
  '#FFB74D', // var(--avatar-6)
];

function getGroupColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return GROUP_ICON_COLORS[Math.abs(hash) % GROUP_ICON_COLORS.length];
}

function getInitial(name: string): string {
  return (name[0] || '?').toUpperCase();
}

// -- Loading skeleton ---------------------------------------------------------

const Skeleton: React.FC = () => (
  <div className="animate-pulse space-y-3 px-4 pt-6">
    <div className="h-8 rounded w-32" style={{ backgroundColor: 'var(--bg-secondary)' }} />
    {[0, 1, 2].map((i) => (
      <div
        key={i}
        className="h-20 rounded-xl"
        style={{ backgroundColor: 'var(--bg-secondary)' }}
      />
    ))}
  </div>
);

// -- Create Group Sheet -------------------------------------------------------

interface CreateGroupSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; description: string }) => void;
  isCreating: boolean;
}

const CreateGroupSheet: React.FC<CreateGroupSheetProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isCreating,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = () => {
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), description: description.trim() });
    setName('');
    setDescription('');
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Создать группу" height="70%">
      <div className="space-y-4 pt-4">
        {/* Name */}
        <div>
          <label
            className="block text-[13px] font-medium mb-1"
            style={{ color: 'var(--text-secondary)' }}
          >
            Название *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Группа начинающих"
            disabled={isCreating}
            className="w-full h-11 px-3 rounded-xl border-none outline-none text-[17px]"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
            }}
          />
        </div>

        {/* Description */}
        <div>
          <label
            className="block text-[13px] font-medium mb-1"
            style={{ color: 'var(--text-secondary)' }}
          >
            Описание
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Описание группы..."
            rows={3}
            disabled={isCreating}
            className="w-full px-3 py-2 rounded-xl border-none outline-none text-[17px] resize-none"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
            }}
          />
        </div>

        {/* Submit */}
        <div className="pt-2 pb-4">
          <Button
            variant="primary"
            fullWidth
            onClick={handleSubmit}
            disabled={!name.trim()}
            loading={isCreating}
          >
            Создать
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
};

// -- Group Card ---------------------------------------------------------------

interface GroupCardProps {
  name: string;
  description?: string;
  color: string;
  athleteCount?: number;
  schedule?: string;
  onClick: () => void;
}

const GroupCard: React.FC<GroupCardProps> = ({
  name,
  description,
  color,
  athleteCount,
  onClick,
}) => {
  return (
    <Card onClick={onClick}>
      <div className="flex items-center gap-3">
        {/* Icon */}
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${color}18` }}
        >
          <span
            className="text-[20px] font-bold"
            style={{ color }}
          >
            {getInitial(name)}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span
              className="text-[17px] font-semibold truncate"
              style={{ color: 'var(--text-primary)' }}
            >
              {name}
            </span>
            {athleteCount !== undefined && (
              <span
                className="text-[13px] flex-shrink-0"
                style={{ color: 'var(--text-hint)' }}
              >
                {athleteCount}
              </span>
            )}
          </div>
          {description && (
            <div
              className="text-[13px] truncate"
              style={{ color: 'var(--text-hint)' }}
            >
              {description}
            </div>
          )}
        </div>

        {/* Chevron */}
        <ChevronRight
          size={16}
          className="flex-shrink-0"
          style={{ color: 'var(--text-hint)' }}
        />
      </div>
    </Card>
  );
};

// -- Main component -----------------------------------------------------------

export function GroupsScreen() {
  const currentAssistantId = useAuthStore((s) => s.currentAssistantId);
  const { pushScreen } = useTabNavigationStore();
  const queryClient = useQueryClient();

  const { maxGroups } = useSubscriptionLimits();

  // -- Local state -----------------------------------------------------------

  const [showCreateSheet, setShowCreateSheet] = useState(false);

  // -- Data queries ----------------------------------------------------------

  const { data: groups, isLoading } = useQuery({
    queryKey: ['groups', currentAssistantId],
    queryFn: () => groupsApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId,
  });

  const { data: allAthletes } = useQuery({
    queryKey: ['athletes', currentAssistantId],
    queryFn: () => athletesApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId,
  });

  // Count athletes per group
  const athleteCountByGroup = useMemo(() => {
    const counts: Record<string, number> = {};
    if (allAthletes) {
      allAthletes.forEach((a: Athlete) => {
        if (a.group_id) {
          counts[a.group_id] = (counts[a.group_id] || 0) + 1;
        }
      });
    }
    return counts;
  }, [allAthletes]);

  // -- Create mutation -------------------------------------------------------

  const createMutation = useMutation({
    mutationFn: (data: { name: string; description: string }) =>
      groupsApi.create(currentAssistantId!, {
        name: data.name,
        description: data.description || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups', currentAssistantId] });
      setShowCreateSheet(false);
    },
    onError: (error) => {
      console.error('Failed to create group:', error);
      alert('Не удалось создать группу. Попробуйте еще раз.');
    },
  });

  // -- Derived state ---------------------------------------------------------

  const currentCount = groups?.length ?? 0;
  const canCreateMore = maxGroups === -1 || currentCount < maxGroups;

  // -- Loading ---------------------------------------------------------------

  if (isLoading) {
    return <Skeleton />;
  }

  // -- No assistant selected -------------------------------------------------

  if (!currentAssistantId) {
    return (
      <EmptyState
        icon={Compass}
        title="Направление не выбрано"
        description="Выберите или создайте направление, чтобы начать работу с группами"
        primaryAction={{
          label: 'Выбрать направление',
          onClick: () => {
            try {
              window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
            } catch {}
            // Open direction switcher
            const { openDirectionSwitcher } = useTabNavigationStore.getState();
            openDirectionSwitcher();
          },
        }}
      />
    );
  }

  // -- Empty state -----------------------------------------------------------

  if (!groups || groups.length === 0) {
    return (
      <>
        <EmptyState
          icon={Layers}
          title="Нет групп"
          description="Создайте первую тренировочную группу для организации работы"
          primaryAction={
            canCreateMore
              ? {
                  label: 'Создать группу',
                  onClick: () => setShowCreateSheet(true),
                }
              : undefined
          }
        />
        {!canCreateMore && (
          <div className="px-6 -mt-8 text-center">
            <p
              className="text-[13px]"
              style={{ color: 'var(--text-hint)' }}
            >
              Лимит групп исчерпан.{' '}
              <button
                onClick={() => pushScreen('subscription-manage' as any)}
                className="underline"
                style={{ color: 'var(--accent)' }}
              >
                Обновите тариф
              </button>
            </p>
          </div>
        )}
        <CreateGroupSheet
          isOpen={showCreateSheet}
          onClose={() => setShowCreateSheet(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          isCreating={createMutation.isPending}
        />
      </>
    );
  }

  // -- List ------------------------------------------------------------------

  return (
    <div
      className="min-h-screen pb-24"
      style={{ backgroundColor: 'var(--bg-primary)' }}
    >
      <div className="px-4 pt-4">
        {/* Header */}
        <Header
          title="Группы"
          action={
            canCreateMore
              ? {
                  label: '+ Создать',
                  onClick: () => setShowCreateSheet(true),
                }
              : undefined
          }
        />

        {/* Group cards */}
        <div className="space-y-3">
          {groups.map((group) => (
            <GroupCard
              key={group.id}
              name={group.name}
              description={group.description}
              color={getGroupColor(group.id)}
              athleteCount={athleteCountByGroup[group.id] || 0}
              onClick={() =>
                pushScreen('group-detail', { groupId: group.id })
              }
            />
          ))}
        </div>

        {/* Limit banner */}
        {!canCreateMore && (
          <div className="mt-4">
            <Card>
              <p
                className="text-[13px]"
                style={{ color: 'var(--text-hint)' }}
              >
                Создано {currentCount} из {maxGroups} групп.{' '}
                <button
                  onClick={() => pushScreen('subscription-manage' as any)}
                  className="underline"
                  style={{ color: 'var(--accent)' }}
                >
                  Обновите тариф
                </button>{' '}
                для добавления.
              </p>
            </Card>
          </div>
        )}
      </div>

      {/* FAB */}
      {canCreateMore && (
        <FAB
          onClick={() => setShowCreateSheet(true)}
          icon={<Plus size={28} color="#FFFFFF" strokeWidth={2.5} />}
        />
      )}

      {/* Create Sheet */}
      <CreateGroupSheet
        isOpen={showCreateSheet}
        onClose={() => setShowCreateSheet(false)}
        onSubmit={(data) => createMutation.mutate(data)}
        isCreating={createMutation.isPending}
      />
    </div>
  );
}
